/**
 * useAccess — TanStack Query hooks for the access agents' read surfaces and the
 * shared cache keys the mutations invalidate.
 *
 * ACL mutations are SERVER-AUTHORITATIVE (frontend/CLAUDE.md clinical-safety
 * carve-out): the connected wrappers never optimistically flip access — they
 * call the REST endpoint, then invalidate these keys so the surface reflects the
 * committed graph. All keys live here so a wrapper and a mutation can't disagree.
 */

import { useMemo } from 'react';
import { AUTHORIZATION_QUERY } from '@/lib/query-config'
import { useQueries } from '@tanstack/react-query';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { ApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { localId, referenceLabel } from '@/lib/reference';
import { listResourceShares } from '@/api/endpoints/sharing';
import {
  getAccessOverview,
  listAllAccessResources,
  listMyAccessRequests,
  listUserRoles,
  type AccessOverviewResponse,
  type AccessRef,
  type AccessResourceItemWire,
  type UnitAccessResponse,
  type UnitGrant,
} from '@/api/endpoints/access';
import {
  listAccessRequestInbox,
  listLocationAccessRequests,
  type AccessRequest,
  type AccessRequestListItem,
} from '@/api/endpoints/accessRequests';
import { listTeamMembers, listTeamsStaffingLocation } from '@/api/endpoints/teams';

export const accessKeys = {
  all: ['access'] as const,
  overview: () => [...accessKeys.all, 'overview'] as const,
  resources: (type: string) => [...accessKeys.all, 'resources', type] as const,
  explanation: (resource: string) => [...accessKeys.all, 'explanation', resource] as const,
  myRequests: () => [...accessKeys.all, 'my-requests'] as const,
  unit: (locationId: string) => [...accessKeys.all, 'unit', locationId] as const,
  inbox: () => [...accessKeys.all, 'inbox'] as const,
};

/** GET /me/access/overview — the personal dashboard. */
export function useAccessOverview() {
  return useAuthenticatedQuery<AccessOverviewResponse, ApiError>({
    queryKey: accessKeys.overview(),
    queryFn: () => getAccessOverview(),
    retry: 1,
    retryDelay: 1000,
    ...AUTHORIZATION_QUERY,
  });
}

/**
 * One reachable-resources query per resource type (from the overview counts),
 * each walking all pages. The wrapper concatenates the results into the
 * view-model. `useQueries` keeps every type independently cached + retried.
 */
export function useAccessResourcesByType(types: string[]) {
  return useQueries({
    queries: types.map((type) => ({
      queryKey: accessKeys.resources(type),
      queryFn: (): Promise<AccessResourceItemWire[]> => listAllAccessResources(type),
      retry: 1,
      retryDelay: 1000,
      refetchOnWindowFocus: false,
    })),
  });
}

/** GET /access-requests — the caller's own outgoing requests. */
export function useMyAccessRequests() {
  return useAuthenticatedQuery<AccessRequestListItem[], ApiError>({
    queryKey: accessKeys.myRequests(),
    queryFn: () => listMyAccessRequests(),
    retry: 1,
    retryDelay: 1000,
    ...AUTHORIZATION_QUERY,
  });
}

/**
 * A unit's access picture, composed client-side from the platform's generic
 * facts: the teams that staff it, their members, each member's roles, the
 * grants held on the unit, and the requests waiting on a decision.
 *
 * There is deliberately no server-side endpoint for this shape. A per-unit
 * access view-model is what one agent surface wants, not a platform resource,
 * and a bespoke join on a location path could never serve an agent nobody has
 * built yet. The same composition backs the access MCP server's
 * `list_unit_access` tool, over the same four endpoints.
 *
 * One query rather than a fan-out of hooks: the answer needs every leg, so a
 * partial picture must not render. A missing leg fails the query and the
 * surface shows its error state — a lead reading a roster that silently
 * dropped a grant is worse than a lead reading an error.
 */
export function useUnitAccess(locationId: string | undefined) {
  return useAuthenticatedQuery<UnitAccessResponse, ApiError>({
    queryKey: accessKeys.unit(locationId ?? ''),
    queryFn: () => composeUnitAccess(locationId as string),
    enabled: !!locationId,
    // A denial is not transient — re-asking for permission just refused only
    // doubles the console noise and the nurse's wait before she is told.
    retry: (failureCount, err) => !isUnitAuthorityDenied(err) && failureCount < 1,
    retryDelay: 1000,
    ...AUTHORIZATION_QUERY,
  });
}

async function composeUnitAccess(locationId: string): Promise<UnitAccessResponse> {
  const [teams, grants, requests] = await Promise.all([
    listTeamsStaffingLocation(locationId),
    listResourceShares('node', locationId),
    listLocationAccessRequests(locationId),
  ]);

  const memberships = (
    await Promise.all(teams.map((team) => listTeamMembers(team.id)))
  ).flat();

  // One entry per person, even when they staff the unit through two teams.
  const userIds = [...new Set(memberships.map((m) => m.user))];
  const rolesByUser = new Map(
    await Promise.all(
      userIds.map(async (id) => [id, await listUserRoles(id)] as const),
    ),
  );

  const grantsByUser = new Map<string, UnitGrant[]>();
  for (const grant of grants) {
    if (grant.principal_type !== 'user') continue;
    const held = grantsByUser.get(grant.principal_id) ?? [];
    held.push({ relation: grant.access_level, expires_at: grant.expires_at });
    grantsByUser.set(grant.principal_id, held);
  }

  const openByUser = new Map<string, AccessRequest[]>();
  for (const request of requests) {
    if (request.status !== 'pending') continue;
    const open = openByUser.get(request.requested_by) ?? [];
    open.push(request);
    openByUser.set(request.requested_by, open);
  }

  // Names come from the membership rows, which carry a resolved user_ref for
  // every member. Reading them off the grants instead left every member who
  // staffs the unit through a team — rather than a direct grant on it — with no
  // name at all, which is most of a roster.
  const nameByUser = new Map(
    memberships.map((m) => [m.user, referenceLabel(m.user_ref)] as const).filter(([, n]) => n),
  );

  return {
    location_id: locationId,
    members: userIds.map((id) => ({
      user_id: id,
      name: nameByUser.get(id),
      roles: rolesByUser.get(id) ?? [],
      node_grants: grantsByUser.get(id) ?? [],
      open_requests: openByUser.get(id) ?? [],
    })),
  };
}

/**
 * True when a failure is the server saying the caller does not manage this
 * unit — the one refusal a team board must SHOW rather than retry.
 *
 * The message that comes back is the actionable one ("Ask <lead> to assign
 * you, or submit an access request for this unit"), and it was going only to
 * the console: the nurse saw an empty or retrying board and no way forward.
 */
export function isUnitAuthorityDenied(err: unknown): err is ApiError {
  return (
    err instanceof ApiError &&
    err.status === 403 &&
    (err.code === 'UNIT_LEAD_REQUIRED' || err.code === 'ACCESS_DENIED')
  );
}

/** GET /access-requests?decidable_by=me — the approver queue. */
export function useAccessRequestInbox() {
  return useAuthenticatedQuery<{ requests: AccessRequestListItem[]; next: string | null }, ApiError>({
    queryKey: accessKeys.inbox(),
    queryFn: () => listAccessRequestInbox(),
    retry: 1,
    retryDelay: 1000,
    ...AUTHORIZATION_QUERY,
  });
}


/** The unit scope a team board (Team Access, Allocation) governs. */
export type UnitScope =
  | { status: 'loading' }
  | { status: 'error'; retry: () => void }
  /**
   * Caller governs no unit — actionable empty state. `profileUnitLabel` is
   * set when she DOES have a workplace, just not one she leads (the ward she
   * works on and the ward she manages are different facts, #2520): the
   * empty-scope UI names it instead of implying she has no unit at all.
   */
  | { status: 'none'; profileUnitLabel?: string }
  /** Several governable units, nothing selected yet — render the chooser. */
  | { status: 'choose'; options: AccessRef[] }
  | { status: 'ready'; unit: AccessRef; options: AccessRef[] };

/**
 * Resolve a team board's unit scope. Precedence:
 *   1. an explicit selection (chooser/switcher pick, or the session's unit link),
 *   2. the caller's own profile unit (the classic lead — today's behaviour),
 *   3. the managed-units catalogue (`can_manage` via GET /me/access/overview):
 *      exactly one → auto-selected; several → `choose`; none → `none`.
 * The caller owns the selection state (a session-linked board persists it
 * server-side via PUT /sessions/{id}/location; a session-less board keeps it local),
 * so this hook stays a pure resolver.
 */
/**
 * The scope derivation, as a pure function of what the caller has: the units
 * she MANAGES (the access overview), her PROFILE unit, and any explicit
 * selection. Separated from the hook so the rule can be stated in a test —
 * this is where a wrong answer turns into a board firing reads it will be
 * refused, which is what it did.
 */
export function deriveUnitScope(input: {
  selectedId?: string;
  profileUnitId?: string;
  profileUnitLabel?: string;
  managed: AccessRef[];
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}): UnitScope {
  const { selectedId, profileUnitId, profileUnitLabel, managed: options, isLoading, isError, retry } = input;
  if (selectedId) {
    const selected = options.find((u) => u.id === selectedId);
    if (selected) return { status: 'ready', unit: selected, options };
    if (isLoading) return { status: 'loading' };
    // A selection that isn't (or is no longer) in the managed catalogue —
    // e.g. a session linked before an authority change. Honour it for the
    // read (the board endpoint re-checks server-side) without switch options.
    return { status: 'ready', unit: { type: 'node', id: selectedId }, options };
  }
  // The profile unit is where the nurse WORKS. It is not, by itself, a unit
  // she LEADS — those are `managed_locations`, and #2520 severed the two for
  // good (ward ownership confers nothing; authority is a grant). Treating the
  // profile unit as scope regardless sent every team board straight at a unit
  // the caller does not manage: four reads, four 403s (UNIT_LEAD_REQUIRED /
  // ACCESS_DENIED), repeated on every refetch, and a board that could never
  // fill — while the sentence saying who could grant her access went to the
  // console.
  if (profileUnitId) {
    const managedProfileUnit = options.find((u) => u.id === profileUnitId);
    if (managedProfileUnit) return { status: 'ready', unit: managedProfileUnit, options };
  }
  if (isLoading) return { status: 'loading' };
  if (isError) return { status: 'error', retry };
  if (options.length === 1) return { status: 'ready', unit: options[0], options };
  if (options.length > 1) return { status: 'choose', options };
  return { status: 'none', profileUnitLabel: profileUnitId ? profileUnitLabel : undefined };
}

export function useUnitScope(selectedId?: string): UnitScope {
  const me = useAuthStore((s) => s.user);
  const profileUnitId = localId(me?.locationRef, 'Location') ?? undefined;
  const profileUnitLabel = me?.locationRef?.display;
  const overview = useAccessOverview();
  const { data, isLoading, isError, refetch } = overview;

  return useMemo<UnitScope>(
    () =>
      deriveUnitScope({
        selectedId,
        profileUnitId,
        profileUnitLabel,
        managed: data?.managed_locations ?? [],
        isLoading,
        isError,
        retry: () => void refetch(),
      }),
    [data, isLoading, isError, refetch, selectedId, profileUnitId, profileUnitLabel],
  );
}
